// Mirrors backend CONVERSATION_PROGRESS + PROGRESS_TO_TEMPERATURE — listed cold→hot.
// Keep in sync with packages/agent/src/helpers/leadAssessmentRules.ts.

export type ProgressId =
  | "not_interested"
  | "off_topic"
  | "no_response"
  | "only_hello"
  | "listing_inquiry"
  | "listing_unavailable"
  | "property_shown"
  | "preferences_collected"
  | "search_no_match"
  | "search_match"
  | "appointment_intent";

const PROGRESS_LABELS: Record<ProgressId, string> = {
  not_interested: "No interesado",
  off_topic: "Fuera de tema",
  no_response: "Sin respuesta",
  only_hello: "Solo saludó",
  listing_inquiry: "Consultó inmueble",
  listing_unavailable: "Inmueble no disponible",
  property_shown: "Inmueble mostrado",
  preferences_collected: "Preferencias capturadas",
  search_no_match: "Búsqueda sin coincidencias",
  search_match: "Búsqueda con coincidencias",
  appointment_intent: "Intención de cita",
};

export const progressLabel = (id: string | null | undefined): string | undefined => {
  if (!id) return undefined;
  return PROGRESS_LABELS[id as ProgressId] ?? id;
};

// Pseudo-progress for leads moved to the inactive position manually — no
// assessment ran, so they sit outside the cold→hot scale.
export type LaneProgressId = ProgressId | "unassessed";

// Lanes are the columns of the Inactivos tab. The cold lane (temperature 0)
// bundles the three progresses that all map to T=0 in the backend — they
// represent the same "no engagement" outcome. All other lanes are 1:1.
export type Lane = {
  id: string;
  label: string;
  progressIds: LaneProgressId[];
  // 0..8 from PROGRESS_TO_TEMPERATURE on the backend; -1 = unassessed
  // (outside the heat scale).
  temperature: number;
};

export const LANES: Lane[] = [
  {
    id: "unassessed",
    label: "Sin evaluar",
    progressIds: ["unassessed"],
    temperature: -1,
  },
  {
    id: "no_engagement",
    label: "No interesados",
    progressIds: ["not_interested", "off_topic", "no_response"],
    temperature: 0,
  },
  { id: "only_hello", label: "Solo saludó", progressIds: ["only_hello"], temperature: 1 },
  {
    id: "listing_inquiry",
    label: "Consultó inmueble",
    progressIds: ["listing_inquiry"],
    temperature: 2,
  },
  {
    id: "listing_unavailable",
    label: "Inmueble no disponible",
    progressIds: ["listing_unavailable"],
    temperature: 3,
  },
  {
    id: "property_shown",
    label: "Inmueble mostrado",
    progressIds: ["property_shown"],
    temperature: 4,
  },
  {
    id: "preferences_collected",
    label: "Preferencias capturadas",
    progressIds: ["preferences_collected"],
    temperature: 5,
  },
  {
    id: "search_no_match",
    label: "Búsqueda sin coincidencias",
    progressIds: ["search_no_match"],
    temperature: 6,
  },
  {
    id: "search_match",
    label: "Búsqueda con coincidencias",
    progressIds: ["search_match"],
    temperature: 7,
  },
  {
    id: "appointment_intent",
    label: "Intención de cita",
    progressIds: ["appointment_intent"],
    temperature: 8,
  },
];

// Maps a progress id back to the lane it belongs to (used in phone mode to
// bucket the unified by-phone response into the same lane structure).
export const LANE_BY_PROGRESS: Record<LaneProgressId, Lane> = LANES.reduce((acc, lane) => {
  for (const pid of lane.progressIds) acc[pid] = lane;
  return acc;
}, {} as Record<LaneProgressId, Lane>);

// Visual treatment per temperature bucket. We use 4 buckets so the gradient
// is readable with 9 columns: cold / cool / warm / hot (-1 = unassessed,
// neutral). Tailwind classes are kept here so callers don't have to know the
// mapping.
export const temperatureChipClasses = (t: number): string => {
  if (t < 0) return "bg-neutral-100 text-neutral-600 ring-1 ring-neutral-300";
  if (t <= 0) return "bg-slate-100 text-slate-700 ring-1 ring-slate-300";
  if (t <= 2) return "bg-sky-100 text-sky-800 ring-1 ring-sky-300";
  if (t <= 5) return "bg-amber-100 text-amber-800 ring-1 ring-amber-300";
  return "bg-rose-100 text-rose-800 ring-1 ring-rose-400";
};

export const temperatureBarClasses = (t: number): string => {
  if (t < 0) return "bg-neutral-300";
  if (t <= 0) return "bg-slate-300";
  if (t <= 2) return "bg-sky-400";
  if (t <= 5) return "bg-amber-400";
  return "bg-rose-500";
};
